/**
 * Entity-detail mock data — ported from v0 kunden-view.tsx + policy-detail-sheet.tsx + brokly-data.ts.
 * Single source of truth for Kunden, Policen, Rechnungen, Komm, Dokumente, Schäden, Aufgaben,
 * Compliance, Historie, Versicherer. All IDs match T5 kundeIds for cross-screen links.
 * FRONTEND-ONLY: mock pass, no real API.
 * [Source: docs/stories/t2-entity-detail.md#task-1]
 */
import type {
  Kunde,
  KundeRow,
  Policy,
  PolicyStatus,
  KundeRechnung,
  Komm,
  Dokument,
  Schaden,
  Aufgabe,
  ComplianceItem,
  Ereignis,
  Beziehung,
  Carrier,
} from './types';
import type { StatusTone } from '../../components/ui/StatusDot';

// ── Status meta ─────────────────────────────────────────────────────────────

// KUNDE_STATUS_META moved to features/customers/customer-status-meta.ts with E1:
// it is the app's real status vocabulary (and the dropdown's options), so it must
// not live in a mock fixture that live screens import.

export const POLICY_STATUS_META: Record<PolicyStatus, { label: string; tone: StatusTone }> = {
  aktiv: { label: 'Aktiv', tone: 'ok' },
  renewal: { label: 'In Renewal', tone: 'decide' },
  sistiert: { label: 'Sistiert', tone: 'neutral' },
  gekuendigt: { label: 'Gekündigt', tone: 'alert' },
};

// ── Kunden ──────────────────────────────────────────────────────────────────

// E1: `status` now carries the real lifecycle values (the invented 'prospect' is
// gone). `statusSince` is the E4 date the current status has applied since.
export const KUNDEN: Kunde[] = [
  { id: 'k-basler', subjectType: 'firma', name: 'Basler Chemie AG', branche: 'Chemie & Pharma', status: 'aktiv', risiko: true, betreuer: 'Lara Meier', kontakt: 'r.meier@baslerchemie.ch', kundennummer: 'K-000001', ort: 'Basel', kanton: 'BS', statusSince: '2016-04-18T09:00:00.000Z', allowedTransitions: ['gekuendigt'] },
  { id: 'k-geneve', subjectType: 'firma', name: 'Genève Maritime SA', branche: 'Transport & Logistik', status: 'aktiv', betreuer: 'Sophie Roy', kontakt: 'contact@geneve-maritime.ch', kundennummer: 'K-000002', ort: 'Genève', kanton: 'GE', statusSince: '2019-09-02T09:00:00.000Z', allowedTransitions: ['gekuendigt'] },
  { id: 'k-alpina', subjectType: 'firma', name: 'Alpina Pharma AG', branche: 'Chemie & Pharma', status: 'aktiv', betreuer: 'Tomas Brunner', kontakt: 'insurance@alpina-pharma.ch', kundennummer: 'K-000003', ort: 'Zug', kanton: 'ZG', statusSince: '2014-11-25T09:00:00.000Z', allowedTransitions: ['gekuendigt'] },
  { id: 'k-bergland', subjectType: 'firma', name: 'Bergland Logistik AG', branche: 'Transport & Logistik', status: 'aktiv', risiko: true, betreuer: 'Lara Meier', kontakt: 'f.huber@bergland-log.ch', kundennummer: 'K-000004', ort: 'Chur', kanton: 'GR', statusSince: '2021-02-08T09:00:00.000Z', allowedTransitions: ['gekuendigt'] },
  { id: 'k-zpaeg', subjectType: 'firma', name: 'Zürcher Präzision AG', branche: 'Maschinenbau', status: 'aktiv', betreuer: 'Sophie Roy', kontakt: 'admin@zpraezision.ch', kundennummer: 'K-000005', ort: 'Zürich', kanton: 'ZH', statusSince: '2012-06-14T09:00:00.000Z', allowedTransitions: ['gekuendigt'] },
  { id: 'k-leman', subjectType: 'firma', name: 'Lac Léman Hôtels SA', branche: 'Gastgewerbe', status: 'provisorisch', betreuer: 'Tomas Brunner', kontakt: 'office@lemanhotels.ch', kundennummer: 'K-000006', ort: 'Lausanne', kanton: 'VD', statusSince: '2026-07-30T09:00:00.000Z', allowedTransitions: ['aktiv'] },
  { id: 'k-ticino', subjectType: 'firma', name: 'Ticino Bau AG', branche: 'Baugewerbe', status: 'inaktiv', betreuer: 'Lara Meier', kontakt: 'info@ticinobau.ch', kundennummer: 'K-000007', ort: 'Lugano', kanton: 'TI', statusSince: '2010-01-20T09:00:00.000Z', allowedTransitions: [] },
];

// ── Policen ─────────────────────────────────────────────────────────────────

export const POLICIES: Policy[] = [
  { id: 'p1', nr: 'POL-339-2266', kunde: 'Basler Chemie AG', kundeId: 'k-basler', versicherer: 'CSS', sparte: 'Betriebshaftpflicht', praemie: 48200, status: 'renewal', renewalTage: 4, beginn: '01.01.2024', bezeichnung: 'Betriebshaftpflicht · CSS', paymentFrequency: 'jaehrlich' },
  { id: 'p7', nr: 'POL-771-5521', kunde: 'Basler Chemie AG', kundeId: 'k-basler', versicherer: 'Mobiliar', sparte: 'Betriebsunterbruch', praemie: 54300, status: 'aktiv', renewalTage: 74, beginn: '01.03.2024' },
  { id: 'p2', nr: 'POL-118-7740', kunde: 'Genève Maritime SA', kundeId: 'k-geneve', versicherer: 'Swiss Life', sparte: 'Transportversicherung', praemie: 73900, status: 'renewal', renewalTage: 2, beginn: '01.07.2023' },
  { id: 'p9', nr: 'POL-145-8830', kunde: 'Genève Maritime SA', kundeId: 'k-geneve', versicherer: 'Helvetia', sparte: 'Gebäudeversicherung', praemie: 22600, status: 'aktiv', renewalTage: 188, beginn: '01.05.2024' },
  { id: 'p3', nr: 'POL-551-9023', kunde: 'Alpina Pharma AG', kundeId: 'k-alpina', versicherer: 'Helvetia', sparte: 'Sachversicherung', praemie: 31500, status: 'aktiv', renewalTage: 6, beginn: '01.04.2024' },
  { id: 'p11', nr: 'POL-523-1902', kunde: 'Alpina Pharma AG', kundeId: 'k-alpina', versicherer: 'AXA', sparte: 'Betriebshaftpflicht', praemie: 64100, status: 'aktiv', renewalTage: 41, beginn: '01.08.2023' },
  { id: 'p15', nr: 'POL-771-3340', kunde: 'Alpina Pharma AG', kundeId: 'k-alpina', versicherer: 'Helvetia', sparte: 'Cyber', praemie: 12900, status: 'renewal', renewalTage: 58, beginn: '01.10.2023' },
  { id: 'p4', nr: 'POL-883-4471', kunde: 'Bergland Logistik AG', kundeId: 'k-bergland', versicherer: 'AXA', sparte: 'Flottenversicherung', praemie: 96400, status: 'renewal', renewalTage: 21, beginn: '01.01.2023' },
  { id: 'p13', nr: 'POL-662-8841', kunde: 'Bergland Logistik AG', kundeId: 'k-bergland', versicherer: 'Swiss Life', sparte: 'Kollektiv-Krankentaggeld', praemie: 142800, status: 'aktiv', renewalTage: 132, beginn: '01.01.2024' },
  { id: 'p5', nr: 'POL-204-1185', kunde: 'Zürcher Präzision AG', kundeId: 'k-zpaeg', versicherer: 'Zurich', sparte: 'Cyber', praemie: 18750, status: 'aktiv', renewalTage: 27, beginn: '15.09.2024' },
  { id: 'p12', nr: 'POL-410-6677', kunde: 'Zürcher Präzision AG', kundeId: 'k-zpaeg', versicherer: 'AXA', sparte: 'Maschinenversicherung', praemie: 37400, status: 'renewal', renewalTage: 33, beginn: '01.02.2024' },
  { id: 'p14', nr: 'POL-902-7745', kunde: 'Zürcher Präzision AG', kundeId: 'k-zpaeg', versicherer: 'CSS', sparte: 'Rechtsschutz', praemie: 9200, status: 'aktiv', renewalTage: 210, beginn: '01.06.2024' },
];

// ── Rechnungen ──────────────────────────────────────────────────────────────

export const RECHNUNGEN: KundeRechnung[] = [
  { id: 'r1', kundeId: 'k-basler', nr: 'RG-2026-0412', datum: '02.06.2026', betrag: 12050, status: { tone: 'decide', label: 'Offen' } },
  { id: 'r2', kundeId: 'k-basler', nr: 'RG-2026-0388', datum: '18.05.2026', betrag: 13575, status: { tone: 'ok', label: 'Bezahlt' } },
  { id: 'r3', kundeId: 'k-geneve', nr: 'RG-2026-0401', datum: '27.05.2026', betrag: 18475, status: { tone: 'info', label: 'In Klärung' } },
  { id: 'r4', kundeId: 'k-alpina', nr: 'RG-2026-0395', datum: '21.05.2026', betrag: 7875, status: { tone: 'ok', label: 'Bezahlt' } },
  { id: 'r5', kundeId: 'k-alpina', nr: 'RG-2026-0420', datum: '08.06.2026', betrag: 16025, status: { tone: 'decide', label: 'Offen' } },
  { id: 'r6', kundeId: 'k-bergland', nr: 'RG-2026-0377', datum: '12.05.2026', betrag: 35700, status: { tone: 'alert', label: 'Überfällig' } },
  { id: 'r7', kundeId: 'k-zpaeg', nr: 'RG-2026-0410', datum: '01.06.2026', betrag: 9350, status: { tone: 'ok', label: 'Bezahlt' } },
];

// ── Kommunikation ───────────────────────────────────────────────────────────

export const KOMM: Komm[] = [
  { id: 'm1', kundeId: 'k-basler', betreff: 'Renewal Betriebshaftpflicht: Rückfrage Deckungssumme', richtung: 'ein', datum: 'vor 3 Std.', status: { tone: 'decide', label: 'Braucht Antwort' } },
  { id: 'm2', kundeId: 'k-basler', betreff: 'Jahresgespräch 2026: Terminvorschläge', richtung: 'aus', datum: 'Gestern', status: { tone: 'info', label: 'Gesendet' } },
  { id: 'm3', kundeId: 'k-geneve', betreff: 'Transportschaden: Nachreichung Dokumente', richtung: 'ein', datum: 'vor 1 Tag', status: { tone: 'neutral', label: 'Gelesen' } },
  { id: 'm4', kundeId: 'k-alpina', betreff: 'Cyber-Police: Verlängerungsbestätigung', richtung: 'aus', datum: 'vor 2 Tagen', status: { tone: 'ok', label: 'Versendet' } },
  { id: 'm5', kundeId: 'k-bergland', betreff: 'Mahnung offene Prämie RG-2026-0377', richtung: 'aus', datum: 'Heute', status: { tone: 'decide', label: 'Entwurf' } },
];

// ── Dokumente ───────────────────────────────────────────────────────────────

export const DOKUMENTE: Dokument[] = [
  { id: 'd1', kundeId: 'k-basler', name: 'Police_Betriebshaftpflicht_2024.pdf', typ: 'Police', datum: '01.01.2024' },
  { id: 'd2', kundeId: 'k-basler', name: 'Risikoaudit_2025.pdf', typ: 'Bericht', datum: '14.11.2025' },
  { id: 'd3', kundeId: 'k-geneve', name: 'Transport_AVB_2023.pdf', typ: 'Bedingungen', datum: '01.07.2023' },
  { id: 'd4', kundeId: 'k-alpina', name: 'Cyber_Fragebogen_2026.pdf', typ: 'Formular', datum: '03.02.2026' },
];

// ── Schäden ─────────────────────────────────────────────────────────────────

export const SCHAEDEN: Schaden[] = [
  { id: 's1', kundeId: 'k-geneve', fall: 'Transportschaden Container HL-2207', datum: '21.05.2026', reserve: 48000, status: { tone: 'decide', label: 'In Bearbeitung' } },
  { id: 's2', kundeId: 'k-bergland', fall: 'Auffahrkollision Flotte ZH-44821', datum: '07.04.2026', reserve: 12500, status: { tone: 'info', label: 'Gemeldet' } },
  { id: 's3', kundeId: 'k-basler', fall: 'Wasserschaden Lager Halle 3', datum: '30.01.2026', reserve: 0, status: { tone: 'ok', label: 'Erledigt' } },
];

// ── Aufgaben ─────────────────────────────────────────────────────────────────

export const AUFGABEN: Aufgabe[] = [
  { id: 'a1', kundeId: 'k-basler', titel: 'Renewal-Offerte Betriebshaftpflicht einholen', faelligTage: 4, prioritaet: { tone: 'decide', label: 'Hoch' } },
  { id: 'a2', kundeId: 'k-basler', titel: 'Jahresgespräch terminieren', faelligTage: 12, prioritaet: { tone: 'neutral', label: 'Normal' } },
  { id: 'a3', kundeId: 'k-geneve', titel: 'Schadendokumente nachfassen', faelligTage: 1, prioritaet: { tone: 'alert', label: 'Dringend' } },
  { id: 'a4', kundeId: 'k-bergland', titel: 'Mahnung offene Prämie versenden', faelligTage: 0, prioritaet: { tone: 'decide', label: 'Hoch' } },
];

// ── Compliance ───────────────────────────────────────────────────────────────

export const COMPLIANCE: ComplianceItem[] = [
  { id: 'c1', kundeId: 'k-basler', label: 'KYC / Identifikation', ok: true, hinweis: 'Vollständig, geprüft 02/2026' },
  { id: 'c2', kundeId: 'k-basler', label: 'Beratungsprotokoll', ok: false, hinweis: 'Aktualisierung nach Renewal nötig' },
  { id: 'c3', kundeId: 'k-basler', label: 'Wirtschaftlich Berechtigte', ok: true, hinweis: 'Erklärung vorhanden' },
  { id: 'c4', kundeId: 'k-geneve', label: 'KYC / Identifikation', ok: true, hinweis: 'Vollständig' },
  { id: 'c5', kundeId: 'k-geneve', label: 'Beratungsprotokoll', ok: true, hinweis: 'Aktuell' },
  { id: 'c6', kundeId: 'k-bergland', label: 'KYC / Identifikation', ok: false, hinweis: 'Ausweis-Kopie fehlt' },
];

// ── Historie ─────────────────────────────────────────────────────────────────

export const HISTORIE: Ereignis[] = [
  { id: 'h1', kundeId: 'k-basler', datum: '08.06.2026', text: 'Rechnung RG-2026-0412 eingegangen (CHF 12\'050)' },
  { id: 'h2', kundeId: 'k-basler', datum: '02.06.2026', text: 'KI-Empfehlung erstellt: Renewal Betriebshaftpflicht' },
  { id: 'h3', kundeId: 'k-basler', datum: '18.05.2026', text: 'Police POL-771-5521 verlängert' },
  { id: 'h4', kundeId: 'k-basler', datum: '14.11.2025', text: 'Risikoaudit-Bericht hochgeladen' },
  { id: 'h5', kundeId: 'k-geneve', datum: '27.05.2026', text: 'Schadenmeldung Transport HL-2207 erfasst' },
  { id: 'h6', kundeId: 'k-alpina', datum: '03.02.2026', text: 'Cyber-Fragebogen 2026 versendet' },
];

// ── Beziehungen ──────────────────────────────────────────────────────────────

export const BEZIEHUNGEN: Beziehung[] = [
  { id: 'b1', kundeId: 'k-basler', relatedId: 'v-css', relatedName: 'CSS', relatedTyp: 'versicherer', typ: 'Versicherungsvertrag', rolle: 'Versicherer (2 Policen)' },
  { id: 'b2', kundeId: 'k-basler', relatedId: 'v-mobiliar', relatedName: 'Mobiliar', relatedTyp: 'versicherer', typ: 'Versicherungsvertrag', rolle: 'Versicherer (1 Police)' },
  { id: 'b3', kundeId: 'k-geneve', relatedId: 'v-swiss-life', relatedName: 'Swiss Life', relatedTyp: 'versicherer', typ: 'Versicherungsvertrag', rolle: 'Versicherer (1 Police)' },
];

// ── Versicherer (Carriers) ───────────────────────────────────────────────────

const carrierDefaults = {
  emailSubmission: null,
  logoUrl: null,
  formTemplateRef: null,
  emailSubmissionOwn: null,
  emailSubmissionGlobal: null,
  emailSubmissionSource: null,
  formTemplateRefOwn: null,
  formTemplateRefGlobal: null,
  formTemplateRefSource: null,
  status: 'aktiv' as const,
  city: null,
  canton: null,
  zefixStatus: 'done' as const,
  origin: 'police' as const,
};

export const CARRIERS: Carrier[] = [
  { id: 'v-axa', name: 'AXA', policen: 3, volumen: 197900, nextRenewal: 'in 21 Tagen', versichererId: 'v-axa', ...carrierDefaults, city: 'Winterthur', canton: 'ZH' },
  { id: 'v-swiss-life', name: 'Swiss Life', policen: 2, volumen: 216700, nextRenewal: 'in 2 Tagen', versichererId: 'v-swiss-life', ...carrierDefaults, city: 'Zürich', canton: 'ZH' },
  { id: 'v-helvetia', name: 'Helvetia', policen: 3, volumen: 67000, nextRenewal: 'in 6 Tagen', versichererId: 'v-helvetia', ...carrierDefaults, city: 'St. Gallen', canton: 'SG' },
  { id: 'v-zurich', name: 'Zurich', policen: 1, volumen: 18750, nextRenewal: 'in 27 Tagen', versichererId: 'v-zurich', ...carrierDefaults, city: 'Zürich', canton: 'ZH' },
  { id: 'v-css', name: 'CSS', policen: 2, volumen: 57400, nextRenewal: 'in 4 Tagen', versichererId: 'v-css', ...carrierDefaults, city: 'Luzern', canton: 'LU' },
  { id: 'v-mobiliar', name: 'Mobiliar', policen: 1, volumen: 54300, nextRenewal: 'in 74 Tagen', versichererId: 'v-mobiliar', ...carrierDefaults, status: 'inaktiv', zefixStatus: 'pending', city: 'Bern', canton: 'BE' },
];

// ── Selectors ────────────────────────────────────────────────────────────────

export function buildKundeRows(): KundeRow[] {
  return KUNDEN.map((k) => {
    const policen = POLICIES.filter((p) => p.kundeId === k.id);
    return {
      ...k,
      policen: policen.length,
      jahrespraemie: policen.reduce((acc, p) => acc + (p.praemie ?? 0), 0),
    };
  });
}

export function getKunde(id: string): KundeRow | undefined {
  return buildKundeRows().find((k) => k.id === id);
}

export function getPolicy(id: string): Policy | undefined {
  return POLICIES.find((p) => p.id === id);
}

export function getCarrier(id: string): Carrier | undefined {
  return CARRIERS.find((c) => c.id === id);
}

export function getCarrierByName(name: string): Carrier | undefined {
  return CARRIERS.find((c) => c.name === name);
}

export function policiesForKunde(kundeId: string): Policy[] {
  return POLICIES.filter((p) => p.kundeId === kundeId);
}

export function rechnungenForKunde(kundeId: string): KundeRechnung[] {
  return RECHNUNGEN.filter((r) => r.kundeId === kundeId);
}

export function kommForKunde(kundeId: string): Komm[] {
  return KOMM.filter((m) => m.kundeId === kundeId);
}

export function dokumenteForKunde(kundeId: string): Dokument[] {
  return DOKUMENTE.filter((d) => d.kundeId === kundeId);
}

export function schadenfaelleForKunde(kundeId: string): Schaden[] {
  return SCHAEDEN.filter((s) => s.kundeId === kundeId);
}

export function aufgabenForKunde(kundeId: string): Aufgabe[] {
  return AUFGABEN.filter((a) => a.kundeId === kundeId);
}

export function complianceForKunde(kundeId: string): ComplianceItem[] {
  return COMPLIANCE.filter((c) => c.kundeId === kundeId);
}

export function historieForKunde(kundeId: string): Ereignis[] {
  return HISTORIE.filter((h) => h.kundeId === kundeId);
}

export function beziehungenForKunde(kundeId: string): Beziehung[] {
  return BEZIEHUNGEN.filter((b) => b.kundeId === kundeId);
}

export function policiesForCarrier(carrierName: string): Policy[] {
  return POLICIES.filter((p) => p.versicherer === carrierName);
}
